// src/features/workspace/SavedViewsList.tsx
// Saved views as a flat list: the views you can switch to, and the actions for
// the one you are in.
//
// This was a strip of pills with a paragraph under it explaining that there
// were none yet, then a dropdown of its own. The pills cost a full row for a
// control most people touch once a session; the dropdown could not survive
// living inside the page's settings menu, because a second popover portals to
// the body and the outer menu reads a click in it as a click outside itself.
// So: a list, shown as a section of that menu. The one thing worth seeing
// without opening anything — *which view am I in* — is the section's summary,
// which is why `activeViewName` is computed here and read by the caller.
//
// Presentational only: it knows nothing about TaskView, the store or the
// workspace state. The container maps those onto `SavedViewItem`s and handles
// the writes, so this file stays reusable for any product that grows views.

import { useI18n } from '@react/hooks';
import { cn } from '@core/ui';

export interface SavedViewItem {
  id: string;
  name: string;
  isShared: boolean;
  isDefault: boolean;
  /** Whether the current user may overwrite, re-default or delete this view. */
  canEdit: boolean;
}

interface SavedViewsListProps {
  views: SavedViewItem[];
  activeId: string | null;
  /** The active view no longer matches what the workspace shows. */
  isModified: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error?: unknown;
  onSelect: (id: string | null) => void;
  onSaveCurrent: () => void;
  onSaveAs: () => void;
  onReset: () => void;
  onToggleDefault: (id: string) => void;
  onDelete: (id: string) => void;
  onRetry: () => void;
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

const rowClass =
  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors';

/**
 * The name to show on the row that leads to this list. An id in the URL that no
 * longer resolves — deleted, unshared, another tenant — must not leave it
 * blank, so it falls back to "all".
 */
export function activeViewName(
  views: SavedViewItem[],
  activeId: string | null,
  fallback: string,
): string {
  return views.find((view) => view.id === activeId)?.name ?? fallback;
}

export function SavedViewsList({
  views,
  activeId,
  isModified,
  isLoading,
  isSaving,
  error,
  onSelect,
  onSaveCurrent,
  onSaveAs,
  onReset,
  onToggleDefault,
  onDelete,
  onRetry,
}: SavedViewsListProps) {
  const { t } = useI18n();

  const activeView = views.find((view) => view.id === activeId) ?? null;

  const pick = (run: () => void) => () => run();

  return (
    <>
        <button
          type="button"
          role="menuitem"
          onClick={pick(() => onSelect(null))}
          className={cn(rowClass, !activeView ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent/50')}
        >
          <span className="flex-1 truncate">{t('workspace.savedViews.all')}</span>
          {!activeView && <CheckIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />}
        </button>

        {isLoading && views.length === 0 && (
          <p className="px-2 py-1.5 text-xs text-muted-foreground">{t('common.loading')}</p>
        )}

        {views.map((view) => {
          const selected = view.id === activeId;
          return (
            <button
              key={view.id}
              type="button"
              role="menuitem"
              onClick={pick(() => onSelect(view.id))}
              className={cn(rowClass, selected ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent/50')}
            >
              {view.isDefault && <StarIcon className="h-3 w-3 flex-shrink-0 text-primary" />}
              {view.isShared && <UsersIcon className="h-3 w-3 flex-shrink-0 opacity-70" />}
              <span className="flex-1 truncate">{view.name}</span>
              <span className="sr-only">
                {view.isShared
                  ? t('workspace.savedViews.shared')
                  : t('workspace.savedViews.personal')}
              </span>
              {selected && <CheckIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />}
            </button>
          );
        })}

        {error ? (
          <p className="flex items-center gap-2 px-2 py-1.5 text-xs text-destructive">
            {t('workspace.savedViews.loadError')}
            <button type="button" onClick={onRetry} className="underline hover:no-underline">
              {t('common.retry')}
            </button>
          </p>
        ) : null}

        <div className="my-1 h-px bg-border" />

        {/* Actions always start from what is on screen — that is what makes a
            view a thing you work in rather than a row in an admin table. */}
        {activeView && isModified && activeView.canEdit && (
          <button
            type="button"
            role="menuitem"
            disabled={isSaving}
            onClick={pick(onSaveCurrent)}
            className={cn(rowClass, 'text-foreground hover:bg-accent/50 disabled:opacity-50')}
          >
            {t('workspace.savedViews.save')}
          </button>
        )}
        {activeView && isModified && (
          <button
            type="button"
            role="menuitem"
            disabled={isSaving}
            onClick={pick(onReset)}
            className={cn(rowClass, 'text-muted-foreground hover:bg-accent/50 hover:text-foreground disabled:opacity-50')}
          >
            {t('workspace.savedViews.reset')}
          </button>
        )}
        <button
          type="button"
          role="menuitem"
          disabled={isSaving}
          onClick={pick(onSaveAs)}
          className={cn(rowClass, 'text-foreground hover:bg-accent/50 disabled:opacity-50')}
        >
          {activeView ? t('workspace.savedViews.saveAs') : t('workspace.savedViews.saveCurrent')}
        </button>
        {activeView?.canEdit && (
          <>
            <button
              type="button"
              role="menuitem"
              onClick={pick(() => onToggleDefault(activeView.id))}
              className={cn(rowClass, 'text-foreground hover:bg-accent/50')}
            >
              <StarIcon className={cn('h-3.5 w-3.5', activeView.isDefault ? 'text-primary' : 'text-muted-foreground')} />
              {activeView.isDefault
                ? t('workspace.savedViews.unsetDefault')
                : t('workspace.savedViews.setDefault')}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={pick(() => onDelete(activeView.id))}
              className={cn(rowClass, 'text-destructive hover:bg-destructive/10')}
            >
              {t('workspace.savedViews.delete')}
            </button>
          </>
        )}
    </>
  );
}
